import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const TermsOfService = () => {
  const navigate = useNavigate();

  const sectionStyle = { marginBottom: '48px' };
  const h2Style = { fontSize: '28px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '16px', borderBottom: '2px solid #C9A961', paddingBottom: '8px' };
  const h3Style = { fontSize: '20px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '12px', marginTop: '24px' };
  const pStyle = { marginBottom: '16px' };
  const ulStyle = { marginLeft: '24px', marginBottom: '16px', lineHeight: '1.8' };
  const calloutStyle = { marginBottom: '48px', background: 'rgba(201, 169, 97, 0.05)', padding: '32px', border: '2px solid #C9A961' };

  return (
    <div style={{ minHeight: '100vh', background: '#fffef2' }}>
      <Helmet>
        <title>Terms of Service | Privé Wealth Management</title>
        <meta name="description" content="Terms of Service for Privé Wealth Management, LLC. Review our advisory agreement terms, fee structures, and client responsibilities." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.privewealthmanagement.com/terms" />
        <meta property="og:title" content="Terms of Service | Privé Wealth Management" />
        <meta property="og:description" content="Terms of Service for Privé Wealth Management, LLC. Review our advisory agreement terms, fee structures, and client responsibilities." />
        <meta property="og:url" content="https://www.privewealthmanagement.com/terms" />
      </Helmet>

      {/* Header */}
      <header style={{ background: '#232e19', borderBottom: '1px solid rgba(201, 169, 97, 0.3)', padding: '20px 0', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img
              src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/0u8dfr1l_Prive%CC%81%20WM%20Main%20Logo%202.png"
              alt="Privé Wealth Management"
              style={{ height: '90px', width: 'auto' }}
            />
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              border: '1px solid #C9A961',
              color: '#C9A961',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
            }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '80px 60px 120px' }}>
        <h1 style={{ fontSize: '48px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '16px', textAlign: 'center' }}>
          Terms of Service
        </h1>
        <p style={{ fontSize: '16px', color: '#666', textAlign: 'center', marginBottom: '8px' }}>
          Privé Wealth Management, LLC
        </p>
        <p style={{ fontSize: '16px', color: '#666', textAlign: 'center', marginBottom: '8px' }}>
          www.privewealthmanagement.com
        </p>
        <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '8px' }}>
          Fee-Only Registered Investment Adviser
        </p>
        <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '8px' }}>
          State of Georgia | CRD #339705
        </p>
        <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '8px' }}>
          Version 1.2 | May 2026
        </p>
        <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '60px' }}>
          Effective Date: January 16, 2026 | Last Updated: May 2026
        </p>

        <div style={{ fontSize: '16px', lineHeight: '1.8', color: '#333' }}>

          <section style={sectionStyle}>
            <h2 style={h2Style}>ACCEPTANCE OF TERMS</h2>
            <p style={pStyle}>
              Welcome to the website of Privé Wealth Management, LLC ("Privé Wealth Management," "we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our website located at www.privewealthmanagement.com (the "Website"). By accessing or using this Website, you agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use this Website. Your use of this Website is also governed by our Privacy Policy, which is incorporated into these Terms by reference. The Privacy Policy describes how we collect, use, and protect your information. Our Privacy Policy is available at <a href="/privacy" style={{ color: '#C9A961' }}>https://www.privewealthmanagement.com/privacy</a>. By accessing or using this Website, you also agree to our Privacy Policy.
            </p>
            <p style={pStyle}>
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Website. Your continued use of the Website after changes are posted constitutes your acceptance of the modified Terms. We encourage you to review these Terms periodically.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={h2Style}>WHO MAY USE THIS WEBSITE</h2>

            <h3 style={h3Style}>Eligibility</h3>
            <p style={{ marginBottom: '12px' }}>This Website is intended for:</p>
            <ul style={ulStyle}>
              <li>Individuals who are 18 years of age or older</li>
              <li>Prospective clients seeking information about our services</li>
              <li>Current clients accessing general information and resources</li>
              <li>Professional colleagues and business contacts</li>
            </ul>

            <h3 style={h3Style}>Restrictions</h3>
            <p style={{ marginBottom: '12px' }}>You may not use this Website if:</p>
            <ul style={ulStyle}>
              <li>You are under 18 years of age</li>
              <li>You are prohibited from receiving services from U.S. persons under applicable laws</li>
              <li>Your use would violate any applicable law or regulation</li>
            </ul>

            <h3 style={h3Style}>Geographic Limitations</h3>
            <p style={pStyle}>
              Privé Wealth Management is registered to provide investment advisory services in the State of Georgia. The information on this Website is intended for residents of the United States only. If you are accessing this Website from outside the United States, you do so at your own risk and are responsible for compliance with local laws.
            </p>
          </section>

          <section style={calloutStyle}>
            <h2 style={{ fontSize: '28px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '16px' }}>
              NO INVESTMENT ADVICE PROVIDED ON WEBSITE
            </h2>

            <h3 style={h3Style}>General Information Only</h3>
            <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>
              IMPORTANT: The content on this Website is for general informational and educational purposes only and does not constitute investment advice, financial advice, trading advice, or any other sort of advice.
            </p>
            <p style={{ marginBottom: '12px' }}>The information provided on this Website:</p>
            <ul style={ulStyle}>
              <li>Is not personalized to your individual circumstances</li>
              <li>Is not tailored to your specific financial situation, goals, or risk tolerance</li>
              <li>Should not be relied upon as the sole basis for any investment decision</li>
              <li>Is not a recommendation to buy, sell, or hold any particular security or investment</li>
              <li>Is not an offer to sell or a solicitation to buy any security or investment product</li>
            </ul>

            <h3 style={h3Style}>Educational and Editorial Content</h3>
            <p style={pStyle}>
              This Website may feature educational and editorial content produced by Privé Collective, the firm's editorial division, including articles from The Ledger (newsletter), episodes from The Compound (podcast), and community content from The Commons. All Privé Collective content is general educational material intended to promote financial literacy and is not personalized investment advice. Editorial content reflects general market commentary, financial education concepts, and cultural wealth-building perspectives — not recommendations specific to any individual's financial situation.
            </p>

            <h3 style={h3Style}>No Substitute for Professional Advice</h3>
            <p style={pStyle}>
              You should not make any investment decision without consulting with a qualified investment adviser who can assess your personal situation. The information on this Website is not a substitute for personalized investment advice from a registered investment adviser.
            </p>

            <h3 style={h3Style}>No Client Relationship Created</h3>
            <p style={{ marginBottom: '12px' }}>
              Visiting this Website, reading its content, or contacting us through the Website does not create an investment advisory relationship. An advisory relationship is created only when:
            </p>
            <ul style={ulStyle}>
              <li>You and Privé Wealth Management execute a written Client Advisory Agreement</li>
              <li>You and Privé Wealth Management execute a Client Service Addendum documenting the specific services to be provided</li>
              <li>You provide the required disclosures and documentation</li>
            </ul>
            <p style={pStyle}>
              We accept you as a client in writing. Until a written Client Advisory Agreement and Client Service Addendum are executed, you are not a client of Privé Wealth Management, and we owe you no fiduciary duty.
            </p>

            <h3 style={h3Style}>No Solicitation</h3>
            <p style={pStyle}>
              The content on this Website does not constitute an offer to provide investment advisory services in any jurisdiction where such offer would be unlawful. Privé Wealth Management is currently registered to provide advisory services in the State of Georgia. Residents of other states should consult their state securities regulators regarding whether our services may be offered in their state of residence.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={h2Style}>ACCURACY AND COMPLETENESS OF INFORMATION</h2>
            <p style={pStyle}>
              The information on this Website is provided "as is" without warranties of any kind. While we strive to provide accurate and up-to-date information, we make no representations or warranties regarding the accuracy, reliability, completeness, or timeliness of any content.
            </p>
            <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>
              Past performance is not indicative of future results. All investments carry risk, including the potential loss of principal. No investment strategy can guarantee a profit or protect against loss.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={h2Style}>SMS/TEXT MESSAGING TERMS AND CONDITIONS</h2>

            <h3 style={h3Style}>Program Description</h3>
            <p style={pStyle}>
              Privé Wealth Management, LLC ("Privé Wealth Management") operates an optional SMS text messaging service (the "SMS Service") to support one-to-one, conversational, business-related communications with clients, prospective clients, professional partners, and other business contacts who have provided prior express consent to receive SMS messages. The SMS Service is operated by Privé Wealth Management directly and is not used for marketing, promotional, bulk, or automated commercial messaging. All SMS messages sent through the SMS Service are sent under the brand name Privé Wealth Management.
            </p>

            <h3 style={h3Style}>Message Types</h3>
            <p style={{ marginBottom: '12px' }}>Recipients who opt into the SMS Service may receive:</p>
            <ul style={ulStyle}>
              <li>appointment reminders and confirmations;</li>
              <li>one-to-one client service communications;</li>
              <li>service-related notifications; and</li>
              <li>verification or authentication messages requested by the recipient.</li>
            </ul>

            <h3 style={h3Style}>Message Frequency</h3>
            <p style={pStyle}>
              Msg frequency varies. Recipients typically receive no more than 20 SMS messages per month, though message volume may temporarily increase during active client onboarding, financial planning engagements, Heritage multi-family office active engagements, insurance application periods, year-end tax and planning windows, or other periods of intensive service delivery.
            </p>

            <h3 style={h3Style}>Message and Data Rates</h3>
            <p style={pStyle}>
              Msg &amp; data rates may apply. Standard text messaging rates charged by your wireless carrier will apply to SMS messages sent and received. Privé Wealth Management does not charge additional fees for SMS communications and is not responsible for charges imposed by mobile carriers.
            </p>

            <h3 style={h3Style}>Opt-Out</h3>
            <p style={pStyle}>
              You may opt out of the Privé Wealth Management SMS service at any time by replying <strong>STOP</strong> to any message you receive from Privé Wealth Management. After opting out, you will not receive further messages from this Privé Wealth Management number unless you re-enroll. You may opt back in at any time by replying <strong>START</strong> to the same number, or by contacting Privé Wealth Management directly using the customer support information below.
            </p>

            <h3 style={h3Style}>Customer Support and Customer Care Contact</h3>
            <p style={pStyle}>
              For more assistance with the Privé Wealth Management SMS service, reply <strong>HELP</strong> to any SMS message, call (470) 737-4131, or email info@privewealthmanagement.com.
            </p>
            <p style={pStyle}>
              Msg frequency varies. Reply STOP to opt out from messages from this Privé Wealth Management number. Msg &amp; data rates may apply.
            </p>

            <h3 style={h3Style}>Mobile Information Sharing</h3>
            <p style={pStyle}>
              Mobile opt-in information and SMS consent data will not be shared with third parties or affiliates for any purpose. Privé Wealth Management does not share, sell, rent, lease, or disclose mobile opt-in information, SMS consent data, or mobile phone numbers collected for SMS purposes with any third parties. Categories of mobile information excluded from sharing include text messaging originator opt-in data and consent. This information will not be shared with any third parties.
            </p>

            <h3 style={h3Style}>Security</h3>
            <p style={pStyle}>
              SMS is not encrypted and should not be considered a secure form of communication. Privé Wealth Management will not send sensitive financial information, account numbers, Social Security numbers, passwords, or other sensitive personal information via text message.
            </p>

            <h3 style={h3Style}>Privacy Policy</h3>
            <p style={pStyle}>
              Information collected through the SMS Service is handled in accordance with our Privacy Policy, available at <a href="/privacy" style={{ color: '#C9A961' }}>https://www.privewealthmanagement.com/privacy</a>. By opting into the SMS Service, you acknowledge and agree to these SMS/Text Messaging Terms and Conditions and to our Privacy Policy.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={h2Style}>LIMITATION OF LIABILITY</h2>
            <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>
              Privé Wealth Management, LLC and its owners, officers, employees, and agents shall not be liable for any investment losses, financial damages, or economic harm arising from your use of information on this Website.
            </p>
            <p style={pStyle}>
              To the fullest extent permitted by law, our total liability to you for any claims arising from your use of this Website shall not exceed $100.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={h2Style}>INTELLECTUAL PROPERTY RIGHTS</h2>
            <p style={pStyle}>
              All content on this Website is the property of Privé Wealth Management, LLC and is protected by copyright laws. "Privé Wealth Management," "Privé Heritage," "Privé Collective," the Privé Wealth Management logo and infinity-feather mark, and "Where Financial Freedom Takes Flight" are trademarks of Privé Wealth Management, LLC.
            </p>
            <p style={pStyle}>
              Content produced under the Privé Collective editorial brand, including The Ledger, The Compound, and The Commons, is the intellectual property of Privé Wealth Management, LLC.
            </p>
            <p style={pStyle}>
              Copyright © 2026 Privé Wealth Management, LLC. All rights reserved.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={h2Style}>CONTACT INFORMATION</h2>
            <p style={{ marginBottom: '12px' }}>
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div style={{ background: '#f6f5e8', padding: '24px', marginTop: '16px' }}>
              <p style={{ marginBottom: '8px' }}><strong>Privé Wealth Management, LLC</strong></p>
              <p style={{ marginBottom: '8px' }}>Philippe-Joseph Eustache, Chief Compliance Officer</p>
              <p style={{ marginBottom: '8px' }}>1702 Sweet Barley Way, Grayson, GA 30017</p>
              <p style={{ marginBottom: '8px' }}>Phone: (470) 737-4131</p>
              <p style={{ marginBottom: '8px' }}>Email: peustache@privewealthmanagement.com</p>
              <p>Website: www.privewealthmanagement.com</p>
            </div>
          </section>

          <section style={{ marginBottom: '48px', background: 'rgba(201, 169, 97, 0.1)', padding: '32px', border: '1px solid #C9A961' }}>
            <h2 style={{ fontSize: '24px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '16px' }}>
              ACKNOWLEDGMENT
            </h2>
            <p style={{ marginBottom: '12px', fontWeight: 'bold' }}>BY USING THIS WEBSITE, YOU ACKNOWLEDGE THAT:</p>
            <ul style={{ marginLeft: '24px', lineHeight: '1.8' }}>
              <li>You have read and understood these Terms of Service</li>
              <li>You agree to be bound by these Terms</li>
              <li>You understand that the Website provides general information only, not personalized advice</li>
              <li>You understand that educational and editorial content from Privé Collective is general financial education, not individualized recommendations</li>
              <li>You will not make investment decisions based solely on Website content</li>
              <li>You understand that no advisory relationship is created by visiting the Website</li>
              <li>You accept the limitations of liability and disclaimers set forth in these Terms</li>
            </ul>
          </section>

          <div style={{ textAlign: 'center', padding: '32px 0', borderTop: '2px solid #C9A961', marginTop: '48px' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              © 2026 Privé Wealth Management, LLC. All rights reserved.
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              Privé Wealth Management, LLC | 1702 Sweet Barley Way, Grayson, GA 30017
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              (470) 737-4131 | info@privewealthmanagement.com | www.privewealthmanagement.com
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              CRD #339705
            </p>
            <p style={{ fontSize: '14px', color: '#C9A961', fontStyle: 'italic' }}>
              "Where Financial Freedom Takes Flight"
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: '#f6f5e8', padding: '40px 0', borderTop: '1px solid #bcbbb4' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 60px', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#666' }}>© 2026 Privé Wealth Management, LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
